// The TIMELINE axis (design brief §5.2): horizontal lanes, one per windowed item in the incoming slice
// order, each drawing a start→end bar from `dateRange`. A null end renders an OPEN bar to the extent's
// right edge with an honest open marker (text, not colour-only). A TODAY line sits at asOfDate (omitted
// honestly when null). An overdue bar carries a non-colour-only '· late' marker. A week/month zoom
// toggle changes density only (two is enough). Bars are keyboard-focusable buttons firing onOpenCard;
// the null-dateRange items live in the shared UndatedTray. Geometry comes from view-dates (no new Date).

import { useMemo, useState } from 'react';
import type { WorkBoardProjection, WorkItemRow } from '../../projections.ts';
import {
  barGeometry,
  isOverdue,
  partitionByDateRange,
  timelineExtent,
  todayPosition,
  type TimelineExtent,
} from '../view-dates.ts';
import { UndatedTray } from './UndatedTray.tsx';

type Zoom = 'week' | 'month';

/** The exit states, so an overdue check never flags an already-closed item as late (G-182). */
function exitStateSet(board: WorkBoardProjection): ReadonlySet<string> {
  return new Set(board.exits.map((exit) => exit.state));
}

function windowLabel(item: WorkItemRow): string {
  const range = item.dateRange;
  if (range === null) return '';
  return range.end === null ? `from ${range.start}, open` : `${range.start} to ${range.end}`;
}

function Lane({
  item,
  extent,
  overdue,
  onOpenCard,
}: {
  readonly item: WorkItemRow;
  readonly extent: TimelineExtent;
  readonly overdue: boolean;
  readonly onOpenCard: (itemId: string) => void;
}): React.JSX.Element {
  const geometry = barGeometry(item.dateRange, extent);
  const open = geometry?.open ?? false;
  const late = overdue ? ' · late' : '';
  const marker = open ? ' · open-ended' : '';
  return (
    <div role="group" aria-label={`${item.subjectDisplay} lane`} className="relative h-9">
      <button
        type="button"
        data-bar={item.id}
        data-item={item.id}
        data-item-state={item.state}
        {...(open ? { 'data-open-bar': 'true' } : {})}
        {...(overdue ? { 'data-late': 'true' } : {})}
        onClick={() => onOpenCard(item.id)}
        aria-label={`Open ${item.subjectDisplay} — ${windowLabel(item)}${late}`}
        style={{
          left: `${String((geometry?.left ?? 0) * 100)}%`,
          width: `${String(Math.max((geometry?.width ?? 0) * 100, 4))}%`,
        }}
        className={`absolute top-1 flex h-7 items-center gap-1 overflow-hidden whitespace-nowrap rounded-sm border px-2 text-left text-xs text-text transition-colors hover:border-border-strong ${
          overdue ? 'border-bracken bg-surface' : 'border-border bg-surface-sunken'
        }`}
      >
        <span className="truncate">{item.subjectDisplay}</span>
        {open ? <span className="shrink-0 text-text-subtle">{marker}</span> : null}
        {overdue ? (
          <span data-late-marker="true" className="shrink-0 font-medium text-bracken">
            · late
          </span>
        ) : null}
      </button>
    </div>
  );
}

function ZoomToggle({
  zoom,
  onZoom,
}: {
  readonly zoom: Zoom;
  readonly onZoom: (zoom: Zoom) => void;
}): React.JSX.Element {
  const options: readonly { readonly value: Zoom; readonly label: string }[] = [
    { value: 'week', label: 'Weeks' },
    { value: 'month', label: 'Months' },
  ];
  return (
    <div
      role="group"
      aria-label="Timeline zoom"
      className="inline-flex rounded-sm bg-surface-sunken p-0.5"
    >
      {options.map(({ value, label }) => (
        <button
          key={value}
          type="button"
          data-zoom={value}
          aria-pressed={value === zoom}
          onClick={() => onZoom(value)}
          className={
            'rounded-sm px-3 py-1 text-sm font-medium transition-colors ' +
            (value === zoom ? 'bg-surface text-text' : 'text-text-muted hover:text-text')
          }
        >
          {label}
        </button>
      ))}
    </div>
  );
}

export interface TimelineViewProps {
  readonly board: WorkBoardProjection;
  /** The filtered slice — the same rows every axis renders, in this order (= lane order). */
  readonly items: readonly WorkItemRow[];
  readonly onOpenCard: (itemId: string) => void;
}

/** The lane axis: zoom toggle, the today line over the lanes, then the undated tray. */
export function TimelineView({ board, items, onOpenCard }: TimelineViewProps): React.JSX.Element {
  const [zoom, setZoom] = useState<Zoom>('week');
  const { windowed, undated } = useMemo(() => partitionByDateRange(items), [items]);
  const extent = useMemo(
    () => timelineExtent(windowed, board.asOfDate),
    [windowed, board.asOfDate],
  );
  const exits = useMemo(() => exitStateSet(board), [board]);
  const todayLeft = extent !== null ? todayPosition(board.asOfDate, extent) : null;
  const laneGap = zoom === 'week' ? 'gap-1.5' : 'gap-1';

  return (
    <section aria-label="Timeline" data-axis-view="timeline">
      <div className="mb-3 flex items-center justify-end">
        <ZoomToggle zoom={zoom} onZoom={setZoom} />
      </div>
      {extent !== null && windowed.length > 0 ? (
        <div className="overflow-x-auto pb-2">
          <div className="relative min-w-[36rem]">
            {todayLeft !== null ? (
              <div
                data-today-line={board.asOfDate}
                aria-label={`Today ${board.asOfDate ?? ''}`}
                style={{ left: `${String(todayLeft * 100)}%` }}
                className="pointer-events-none absolute inset-y-0 z-10 w-px bg-border-strong"
              />
            ) : null}
            <div className={`flex flex-col ${laneGap}`}>
              {windowed.map((item) => (
                <Lane
                  key={item.id}
                  item={item}
                  extent={extent}
                  overdue={isOverdue(item, board.asOfDate, exits)}
                  onOpenCard={onOpenCard}
                />
              ))}
            </div>
          </div>
        </div>
      ) : null}
      <UndatedTray items={undated} onOpenCard={onOpenCard} />
    </section>
  );
}
